import React, { useEffect, useState, useCallback } from "react";
import Header from "../components/organisms/Header";
import Loader from "../components/molecules/Loader";
import { useHospDocData } from "../lib/contexts/HospitalDoctorContext";
import {
  getDownloadRequests,
  getDownloadSignedUrl,
  searchDownloadRequests,
} from "../lib/apis/analytics";
import {
  DownloadRequest,
  DownloadRequestsResponse,
  GetDownloadRequestsParams,
} from "../lib/utils/types";
import { toast } from "react-toastify";
import { getCookie } from "../lib/utils/cookies";
import { utcToTimezone, timezoneToUtc } from "../lib/utils/funcs";
import Tooltip from "@mui/material/Tooltip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { IoMdDownload } from "react-icons/io";
import { MdRefresh } from "react-icons/md";
import AnalyticsNavigationTabs from "../components/molecules/AnalyticsNavigationTabs";

const haveDownloadsChanged = (
  prev: DownloadRequest[],
  next: DownloadRequest[]
) => {
  if (prev.length !== next.length) return true;

  for (let i = 0; i < prev.length; i++) {
    const prevItem = prev[i];
    const nextItem = next[i];

    if (
      prevItem.analytics_downloads_id !== nextItem.analytics_downloads_id ||
      prevItem.status !== nextItem.status ||
      prevItem.file_url_key !== nextItem.file_url_key ||
      prevItem.created_at !== nextItem.created_at ||
      prevItem.start_date !== nextItem.start_date ||
      prevItem.end_date !== nextItem.end_date
    ) {
      return true;
    }
  }

  return false;
};

const DownloadCenter = () => {
  const { hospitalID } = useHospDocData();
  const [downloads, setDownloads] = useState<DownloadRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<
    "pending" | "processing" | "processed" | "failed" | "all"
  >("all");
  const [analyticsTypeFilter, setAnalyticsTypeFilter] = useState<string>("");
  const [createdAtFilter, setCreatedAtFilter] = useState<string>("");
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());

  // Analytics type options
  const ANALYTICS_TYPES = {
    BOOKING: 'booking',
    LEADS: 'leads',
    CANCEL_REASON: 'cancel_reason',
    RC_COUNT: 'rc_count',
    RATING: 'rating',
    GOOGLE_REVIEW: 'google_review',
    TOTAL_BOOKINGS: 'total_bookings',
    NEW_PATIENTS: 'new_patients',
    SHOWED_UP: 'showed_up',
    GOOGLE_REVIEWS_CLICKED: 'google_reviews_clicked',
    DOCTOR_ATTENDANCE: 'doctor_attendance',
  };

  const fetchDownloads = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!hospitalID) return;

      if (!options?.silent) {
        setLoading(true);
      }

      try {
        const params: GetDownloadRequestsParams = {
          page: page + 1, // API uses 1-based pagination
          limit: rowsPerPage,
          hospital_id: hospitalID,
          ...(statusFilter !== "all" && { status: statusFilter }),
        };

        // Convert created_at filter from hospital timezone to UTC if provided
        let createdAtUtc = createdAtFilter;
        if (createdAtFilter) {
          const hospitalTimezone = getCookie("timezone") || "Asia/Kolkata";
          // Convert the date from hospital timezone to UTC ISO format
          // The date picker gives us "2025-11-18", we need to convert this to UTC timestamp
          createdAtUtc = timezoneToUtc(
            `${createdAtFilter} 00:00:00`,
            hospitalTimezone,
            "YYYY-MM-DD HH:mm:ss",
            "YYYY-MM-DD"
          );
        }

        let response: DownloadRequestsResponse;
        if (analyticsTypeFilter || createdAtUtc) {
          // Use search endpoint if filters are applied
          response = await searchDownloadRequests({
            ...params,
            ...(analyticsTypeFilter && { analytics_type: analyticsTypeFilter }),
            ...(createdAtUtc && { created_at: createdAtUtc }),
          });
        } else {
          response = await getDownloadRequests(params);
        }

        setDownloads((prev) =>
          haveDownloadsChanged(prev, response.downloads) ? response.downloads : prev
        );
        setTotal((prevTotal) =>
          prevTotal === response.pagination.total
            ? prevTotal
            : response.pagination.total
        );
      } catch (error: any) {
        console.error("Error fetching downloads:", error);
        toast.error(
          error?.response?.data?.message || "Failed to fetch downloads"
        );
      } finally {
        if (!options?.silent) {
          setLoading(false);
        }
      }
    },
    [
      hospitalID,
      page,
      rowsPerPage,
      statusFilter,
      analyticsTypeFilter,
      createdAtFilter,
    ]
  );

  useEffect(() => {
    fetchDownloads();
  }, [fetchDownloads]);

  // Poll for status updates every 3 seconds for pending/processing downloads
  useEffect(() => {
    const hasPendingOrProcessing = downloads.some(
      (d) => d.status === "pending" || d.status === "processing"
    );

    if (!hasPendingOrProcessing) return;

    const interval = setInterval(() => {
      fetchDownloads({ silent: true });
    }, 3000);

    return () => clearInterval(interval);
  }, [downloads, fetchDownloads]);

  const handleDownload = async (download: DownloadRequest) => {
    if (download.status !== "processed") {
      toast.warning("Download is not ready yet. Please wait for processing to complete.");
      return;
    }
    if (!download.file_url_key) {
      toast.info("This file was generated using the previous download flow and is no longer available. Please submit a new download request.");
      return;
    }

    setDownloadingIds((prev) => new Set(prev).add(download.analytics_downloads_id));
    try {
      const response = await getDownloadSignedUrl(download.analytics_downloads_id);
      
      // Trigger download
      const link = document.createElement("a");
      link.href = response.downloadUrl;
      link.download = `${download.analytics_type}_${download.start_date || ""}_${download.end_date || ""}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Download started!");
    } catch (error: any) {
      console.error("Error downloading file:", error);
      toast.error(
        error?.response?.data?.message || "Failed to get download URL"
      );
    } finally {
      setDownloadingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(download.analytics_downloads_id);
        return newSet;
      });
    }
  };

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  const getStatusBadge = (status: string): React.ReactNode => {
    const baseClasses = "px-3 py-1 rounded-full text-xs font-semibold";
    switch (status) {
      case "pending":
        return (
          <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
            Pending
          </span>
        );
      case "processing":
        return (
          <span className={`${baseClasses} bg-blue-100 text-blue-800`}>
            Processing
          </span>
        );
      case "processed":
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800`}>
            Ready
          </span>
        );
      case "failed":
        return (
          <span className={`${baseClasses} bg-red-100 text-red-800`}>
            Failed
          </span>
        );
      default:
        return <span className={baseClasses}>{status}</span>;
    }
  };

  const formatAnalyticsType = (type: string) => {
    return type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const formatDateRange = (start: string | null, end: string | null) => {
    if (!start && !end) return "N/A";
    
    // Get hospital timezone from cookie, default to Asia/Kolkata
    const hospitalTimezone = getCookie("timezone") || "Asia/Kolkata";
    
    if (start && end) {
      // Convert ISO UTC dates to local hospital timezone
      const startFormatted = utcToTimezone(start, hospitalTimezone, "MMM DD, YYYY");
      const endFormatted = utcToTimezone(end, hospitalTimezone, "MMM DD, YYYY");
      return `${startFormatted} - ${endFormatted}`;
    }
    
    return start
      ? utcToTimezone(start, hospitalTimezone, "MMM DD, YYYY")
      : utcToTimezone(end || "", hospitalTimezone, "MMM DD, YYYY");
  };

  const formatDateTime = (isoDate: string) => {
    // Get hospital timezone from cookie, default to Asia/Kolkata
    const hospitalTimezone = getCookie("timezone") || "Asia/Kolkata";
    
    // Convert ISO 8601 UTC date to hospital timezone
    return utcToTimezone(isoDate, hospitalTimezone, "MMM DD, YYYY HH:mm");
  };

  if (!hospitalID) {
    return <Loader hospitalId={hospitalID} />;
  }

  return (
    <div className="w-full h-[100vh] overflow-y-auto side_bar pb-6 relative">
      <Header header value={"Download Center"} />

      <div className="p-7">
        <AnalyticsNavigationTabs hospitalId={hospitalID} className="mb-6" />
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value as any);
                  setPage(0);
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="processed">Ready</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Analytics Type</InputLabel>
              <Select
                value={analyticsTypeFilter}
                label="Analytics Type"
                onChange={(e) => {
                  setAnalyticsTypeFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All Types</MenuItem>
                {Object.entries(ANALYTICS_TYPES).map(([key, value]) => (
                  <MenuItem key={value} value={value}>
                    {formatAnalyticsType(value)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Download Requested On"
              type="date"
              size="small"
              value={createdAtFilter}
              onChange={(e) => {
                setCreatedAtFilter(e.target.value);
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
              helperText="Filter by request date"
            />
          </div>

          <div className="flex gap-2">
            <Button
              variant="contained"
              onClick={() => fetchDownloads()}
              startIcon={<MdRefresh />}
              size="small"
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setStatusFilter("all");
                setAnalyticsTypeFilter("");
                setCreatedAtFilter("");
                setPage(0);
              }}
              size="small"
            >
              Clear Filters
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm">
          {loading ? (
            <div className="flex justify-center items-center min-h-[400px]">
              <Loader hospitalId={hospitalID} />
            </div>
          ) : downloads?.length === 0 ? (
            <div className="flex justify-center items-center min-h-[400px]">
              <p className="text-gray-500">No downloads found.</p>
            </div>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Date Range</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Created At</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {downloads.map((download) => (
                      <TableRow key={download.analytics_downloads_id}>
                        <TableCell>
                          {formatAnalyticsType(download.analytics_type)}
                        </TableCell>
                        <TableCell>
                          {formatDateRange(download.start_date, download.end_date)}
                        </TableCell>
                        <TableCell>{getStatusBadge(download.status)}</TableCell>
                        <TableCell>
                          {formatDateTime(download.created_at)}
                        </TableCell>
                        <TableCell>
                          {download.status === "processed" ? (
                            <Tooltip
                              title={
                                download.file_url_key
                                  ? ""
                                  : "No file stored. Please request a fresh download."
                              }
                            >
                              <span>
                                <button
                                  onClick={() => handleDownload(download)}
                                  disabled={
                                    downloadingIds.has(
                                      download.analytics_downloads_id
                                    ) || !download.file_url_key
                                  }
                                  className="flex items-center justify-center gap-2 px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed w-10"
                                >
                                  {downloadingIds.has(
                                    download.analytics_downloads_id
                                  ) ? (
                                    <CircularProgress size={14} sx={{ color: 'white' }} />
                                  ) : (
                                    <IoMdDownload className="w-4 h-4" />
                                  )}
                                </button>
                              </span>
                            </Tooltip>
                          ) : download.status === "failed" ? (
                            <span className="text-red-600 text-sm">
                              {download.failure_reason || "Failed"}
                            </span>
                          ) : (
                            <span className="text-gray-500 text-sm">
                              {download.status === "pending"
                                ? "Queued..."
                                : "Processing..."}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DownloadCenter;

